import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Drawer, Form, Input, Upload, Radio, Button, Table, Select, Checkbox, Space, Typography, message, Alert, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { PLATFORM_LABEL, REGION_LABEL } from './materialConfig';
import SearchSelect from '../../../components/SearchSelect/SearchSelect';
import { fetchAppGroups, findAppGroup } from './appGroupMock';

const { Text } = Typography;

// 各支付通道可选的商户号（示例）
const MERCHANT_OPTIONS = {
    '微信支付': ['微信商户号 11111', '微信商户号 22222'],
    '支付宝': ['支付宝商户号 33333'],
    'Apple Pay': ['Apple 商户号 A001'],
    'Google Pay': ['Google 商户号 G001'],
    'PayPal': ['PayPal 商户号 P001'],
    'Stripe': ['Stripe 商户号 S001'],
    '空中云汇': ['空中云汇商户号 AW001'],
};
const CN_CHANNELS = ['微信支付', '支付宝'];
const OS_CHANNELS = ['Apple Pay', 'Google Pay', 'PayPal', 'Stripe', '空中云汇'];

const PAY_METHOD_LABEL = { enterprise: '企业自收款', platform: '平台代收款' };

// 项目二阶段已配置的支付通道（按平台区域推导）
const projectChannels = (record) => {
    const set = new Set();
    (record?.targets || []).forEach((t) => {
        const regions = Array.isArray(t.regions) ? t.regions : (t.regions ? [t.regions] : []);
        regions.forEach((rg) => {
            if (rg === 'os') OS_CHANNELS.forEach((c) => set.add(c));
            else if (rg === 'global') { CN_CHANNELS.forEach((c) => set.add(c)); OS_CHANNELS.forEach((c) => set.add(c)); }
            else CN_CHANNELS.forEach((c) => set.add(c));
        });
    });
    return [...set];
};

// 展开「平台 × 区域」为版本行
const buildVersions = (record) => {
    const rows = [];
    (record?.targets || []).forEach((t) => {
        const regions = Array.isArray(t.regions) ? t.regions : (t.regions ? [t.regions] : []);
        regions.forEach((rg) => rows.push({
            key: `${t.platform}-${rg}`,
            platform: t.platform,
            region: rg,
            platformLabel: PLATFORM_LABEL[t.platform] || t.platform,
            regionLabel: REGION_LABEL[rg] || rg,
            appId: '',
            channels: {},
            merchants: {},
        }));
    });
    return rows;
};

// 创建应用抽屉：
// - 归属方式：创建新应用组（默认，当前交互）/ 加入已有应用组（继承展示信息，仅追加版本）
// - 版本列表按定制范围生成：系统平台、AppID、上架区域、支付通道
const CreateAppDrawer = ({ open, record, onClose, onCreated }) => {
    const [form] = Form.useForm();
    const [mode, setMode] = useState('new');
    const [groupId, setGroupId] = useState(null);
    const [payMethod, setPayMethod] = useState('enterprise');
    const [versions, setVersions] = useState([]);
    const channels = record ? projectChannels(record) : [];
    const companyId = record?.companyId;

    const selectedGroup = useMemo(
        () => (companyId && groupId ? findAppGroup(companyId, groupId) : null),
        [companyId, groupId],
    );
    // 稳定引用，避免 SearchSelect 反复触发初始请求
    const fetchGroupOptions = useCallback((keyword) => fetchAppGroups(companyId)(keyword), [companyId]);

    useEffect(() => {
        if (open && record) {
            form.resetFields();
            form.setFieldsValue({ appName: record.appName, payMethod: 'enterprise' });
            setMode('new');
            setGroupId(null);
            setPayMethod('enterprise');
            setVersions(buildVersions(record));
        }
    }, [open, record, form]);

    const setVersion = (key, patch) => setVersions((prev) => prev.map((v) => (v.key === key ? { ...v, ...patch } : v)));

    const handleModeChange = (val) => {
        setMode(val);
        setGroupId(null);
        if (val === 'new') setPayMethod(form.getFieldValue('payMethod') || 'enterprise');
    };

    const handleSelectGroup = (val) => {
        setGroupId(val || null);
        const g = val ? findAppGroup(companyId, val) : null;
        if (g) setPayMethod(g.payMethod);
    };

    const handleSave = async () => {
        try {
            if (mode === 'new') await form.validateFields();
            else if (!groupId) { message.warning('请选择应用组'); return; }
            if (!versions.length) { message.warning('请至少添加一个系统平台'); return; }
            const missing = versions.find((v) => !v.appId.trim());
            if (missing) { message.warning(`请填写「${missing.platformLabel} · ${missing.regionLabel}」的 AppID`); return; }

            const newVersions = versions.map((v) => ({
                platformLabel: v.platformLabel,
                regionLabel: v.regionLabel,
                appId: v.appId.trim(),
            }));

            // 加入已有应用组：不允许与组内已有版本重复（同「系统-区域」）
            if (mode === 'existing' && selectedGroup) {
                const dup = newVersions.find((v) => (selectedGroup.versions || [])
                    .some((gv) => gv.platformLabel === v.platformLabel && gv.regionLabel === v.regionLabel));
                if (dup) { message.warning(`该应用组已存在「${dup.platformLabel} · ${dup.regionLabel}」版本`); return; }
            }

            const result = (mode === 'existing' && selectedGroup)
                ? {
                    id: selectedGroup.id,
                    name: selectedGroup.name,
                    payMethod: selectedGroup.payMethod,
                    versions: [...(selectedGroup.versions || []), ...newVersions],
                }
                : {
                    id: `APP-${Date.now()}`,
                    name: form.getFieldValue('appName'),
                    payMethod,
                    versions: newVersions,
                };

            message.success(mode === 'existing' ? '版本已追加到所选应用组（mock）' : '应用创建成功（mock）');
            if (onCreated) onCreated(result);
            onClose();
        } catch (e) {
            if (e?.errorFields) return;
        }
    };

    const columns = [
        { title: '系统平台', dataIndex: 'platformLabel', width: 100 },
        {
            title: 'AppID', key: 'appId', width: 180,
            render: (_, r) => (
                <Input placeholder={r.platform === 'ios' ? 'iOS Bundle ID（com.xxx.xxx）' : 'Android 包名（com.xxx.xxx）'}
                    value={r.appId} onChange={(e) => setVersion(r.key, { appId: e.target.value })} />
            ),
        },
        { title: '上架区域', dataIndex: 'regionLabel', width: 100 },
        {
            title: '支付通道', key: 'channels',
            render: (_, r) => {
                if (payMethod === 'platform') {
                    return (
                        <Select mode="multiple" style={{ width: '100%' }} placeholder="选择支付通道"
                            value={Object.keys(r.channels)}
                            onChange={(list) => setVersion(r.key, { channels: Object.fromEntries(list.map((c) => [c, true])) })}
                            options={channels.map((c) => ({ label: c, value: c }))} />
                    );
                }
                // 企业自收款：与二阶段已配置支付联动 —— 通道来自二阶段已配置，选中后需指定商户号
                return (
                    <div>
                        {channels.map((c) => {
                            const checked = !!r.channels[c];
                            return (
                                <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                    <Checkbox checked={checked}
                                        onChange={(e) => setVersion(r.key, { channels: { ...r.channels, [c]: e.target.checked } })}>
                                        <Text style={{ fontSize: 13 }}>{c}</Text>
                                    </Checkbox>
                                    {checked && (
                                        <Select size="small" style={{ width: 180 }} placeholder="选择商户号"
                                            value={r.merchants[c]}
                                            onChange={(val) => setVersion(r.key, { merchants: { ...r.merchants, [c]: val } })}
                                            options={(MERCHANT_OPTIONS[c] || []).map((m) => ({ label: m, value: m }))} />
                                    )}
                                </div>
                            );
                        })}
                        <Text type="secondary" style={{ fontSize: 12 }}>通道来自二阶段「支付渠道」已配置项，需指定商户号</Text>
                    </div>
                );
            },
        },
    ];

    return (
        <Drawer
            title={`创建应用 · ${record?.appName || ''}`}
            open={open}
            width={760}
            onClose={onClose}
            footer={<Space style={{ float: 'right' }}><Button onClick={onClose}>取消</Button><Button type="primary" onClick={handleSave}>保存</Button></Space>}
        >
            <Text strong style={{ display: 'block', marginBottom: 8 }}>归属方式</Text>
            <Radio.Group value={mode} onChange={(e) => handleModeChange(e.target.value)} style={{ marginBottom: 16 }}>
                <Space direction="vertical" size={4}>
                    <Radio value="new">创建新应用组<Text type="secondary" style={{ fontSize: 12 }}>（新建一个 App，并添加版本）</Text></Radio>
                    <Radio value="existing">加入已有应用组<Text type="secondary" style={{ fontSize: 12 }}>（在已有 App 下追加版本，信息继承该组）</Text></Radio>
                </Space>
            </Radio.Group>

            {mode === 'new' ? (
                <>
                    <Form form={form} layout="vertical">
                        <Form.Item label="应用名称" name="appName" rules={[{ required: true, message: '请输入应用名称' }]}>
                            <Input placeholder="请输入应用名称" maxLength={40} />
                        </Form.Item>
                        <Form.Item label="应用LOGO" name="appLogo" valuePropName="fileList"
                            getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                            extra="建议尺寸 200*200，支持 png、jpg">
                            <Upload listType="picture-card" maxCount={1} beforeUpload={() => false}>
                                <div><PlusOutlined /><div style={{ marginTop: 8 }}>上传图片</div></div>
                            </Upload>
                        </Form.Item>
                        <Form.Item label="收款方式" name="payMethod" required>
                            <Radio.Group onChange={(e) => setPayMethod(e.target.value)}>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Radio value="enterprise">企业自收款<Text type="secondary" style={{ fontSize: 12 }}>（需配置收款商户号，收益直接进入企业账户）</Text></Radio>
                                    <Radio value="platform">平台代收款<Text type="secondary" style={{ fontSize: 12 }}>（收益到平台，按分润结算）</Text></Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                    </Form>
                </>
            ) : (
                <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>选择应用组</Text>
                    <SearchSelect
                        style={{ width: '100%' }}
                        placeholder="搜索应用组（应用名称 / AppID）"
                        value={groupId}
                        onChange={handleSelectGroup}
                        fetchOptions={fetchGroupOptions}
                        allowClear
                    />
                    {selectedGroup && (
                        <div style={{ marginTop: 12, padding: 12, background: '#fafafa', borderRadius: 8 }}>
                            <Space size={16} wrap>
                                <Text style={{ fontSize: 13 }}>应用名称：<Text strong>{selectedGroup.name}</Text></Text>
                                <Text style={{ fontSize: 13 }}>收款方式：<Tag color={selectedGroup.payMethod === 'platform' ? 'blue' : 'default'} style={{ border: 'none' }}>{PAY_METHOD_LABEL[selectedGroup.payMethod]}</Tag></Text>
                                <Text style={{ fontSize: 13 }}>已有版本：<Tag style={{ border: 'none' }}>{selectedGroup.versions?.length || 0} 个</Tag></Text>
                            </Space>
                        </div>
                    )}
                    <Alert style={{ marginTop: 12 }} type="info" showIcon
                        message="应用名称 / 应用LOGO / 收款方式继承所选应用组，如需修改请到「App 管理」；本次提交仅向该组追加新版本。" />
                </div>
            )}

            <Text strong style={{ display: 'block', marginBottom: 8 }}>版本列表（按定制范围自动生成）</Text>
            <Table columns={columns} dataSource={versions} rowKey="key" pagination={false} size="small" bordered />
        </Drawer>
    );
};

export default CreateAppDrawer;
